import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { OrderClassEnum } from '@modules/orders-module/enums/order-class.enum'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { OrderStateEnum } from '@modules/orders-module/enums/order-state.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { OrdersResponseInterface } from '@modules/orders-module/interfaces/order-response.interface'
import { PaginationComponent } from '@shared/components/common/pagination/pagination.component'
import { BehaviorSubject, Subject, takeUntil } from 'rxjs'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { OrdersSettingsInterface } from '@shared/components/trading/widgets/orders-widget/core/interfaces/orders-settings.interface'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { ColumnsCanceledOrdersEnum } from '@shared/components/trading/widgets/orders-widget/core/enums/stocks/columns-canceled-orders.enum'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { OrderParamsInterface } from '@modules/orders-module/interfaces/order-params.interface'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { NgTemplateOutlet } from '@angular/common'
import { PercentPipe } from '@shared/pipes/percent.pipe'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { TitleOrderTypePipe } from '@modules/orders-module/pipes/title-order-type.pipe'
import { TranslatePipe } from '@ngx-translate/core'
import { TuiBadge, TuiSkeleton } from '@taiga-ui/kit'
import { AssetClassEnum } from '@modules/positions-module/enums/asset-class.enum'
import { CanceledOrdersActionsComponent } from '@shared/components/trading/orders/orders-stocks/tables/canceled-orders/common/canceled-orders-actions/canceled-orders-actions.component'
import { TableColumnComponent } from '@shared/components/common/table/core/components/table-column/table-column.component'
import { TableActionsComponent } from '@shared/components/common/table/core/components/table-actions/table-actions.component'
import { TableFooterComponent } from '@shared/components/common/table/core/components/table-footer/table-footer.component'
import { LiveTickerService } from '@modules/stocks-module/live-ticker/services/live-ticker.service'
import { StocksOrdersService } from '@modules/stocks-module/orders/services/stocks-orders.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { SecretDirective } from '@shared/directives/secret.directive'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'
import { TableComponent } from '@shared/components/common/table/table.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-canceled-orders',
	templateUrl: './canceled-orders.component.html',
	styleUrl: './canceled-orders.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		PaginationComponent,
		HighlightDirective,
		NgTemplateOutlet,
		PercentPipe,
		PricePipe,
		QuantityPipe,
		TitleOrderTypePipe,
		TranslatePipe,
		TuiBadge,
		CanceledOrdersActionsComponent,
		TableColumnComponent,
		TableActionsComponent,
		TableFooterComponent,
		SecretDirective,
		TuiSkeleton,
		TableComponent,
	],
})
export class CanceledOrdersComponent implements OnInit, OnDestroy, OnChanges {
	protected readonly number: NumberConstructor = Number
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderStateEnum: typeof OrderStateEnum = OrderStateEnum
	protected readonly orderClassEnum: typeof OrderClassEnum = OrderClassEnum
	protected readonly assetClassEnum: typeof AssetClassEnum = AssetClassEnum
	protected readonly columnsCanceledOrdersEnum: typeof ColumnsCanceledOrdersEnum = ColumnsCanceledOrdersEnum

	public widget: InputSignal<WidgetInterface<OrdersSettingsInterface> | null> =
		input<WidgetInterface<OrdersSettingsInterface> | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _ordersStocksService: StocksOrdersService = inject<StocksOrdersService>(StocksOrdersService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _liveTickerService: LiveTickerService = inject<LiveTickerService>(LiveTickerService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)
	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)

	protected isLoadingOrders: WritableSignal<boolean> = signal(false)
	protected ordersResponse: WritableSignal<OrdersResponseInterface | null | undefined> = signal(null)
	protected ordersParams: WritableSignal<OrderParamsInterface | undefined> = signal(undefined)

	protected statesColumns: WritableSignal<ColumnInterface<ColumnsCanceledOrdersEnum>[]> = signal([])
	protected needScrollToTop: WritableSignal<boolean> = signal(false)
	protected isSecret: WritableSignal<boolean> = signal<boolean>(false)
	private _listenSecret(): void {
		this._userHelperService.isSecret$.pipe(takeUntil(this._destroy$)).subscribe((value: boolean): void => {
			this.isSecret.set(value)
		})
	}

	private _launchCanceledOrders(): void {
		this._tradingLauncherService
			.launchOrders$(this.orderStateEnum.CANCELED)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				([ordersResponse, isLoading, orderRequest, isNeedUpdate]: [
					OrdersResponseInterface | null | undefined,
					boolean,
					OrderParamsInterface | undefined,
					boolean,
				]): void => {
					if (this.isLoadingOrders() !== isLoading) this.needScrollToTop.set(true)

					this.ordersResponse.set(ordersResponse)
					this.isLoadingOrders.set(isLoading)
					this.ordersParams.set(orderRequest)

					setTimeout((): void => this.needScrollToTop.set(false))
					if (isNeedUpdate) {
						this._ordersStocksHelperService.isNeedUpdateOrderResponseMap
							.get(this.orderStateEnum.CANCELED)
							?.next(false)
						this._getCanceledOrders()
					}
				},
			)
	}

	private _getCanceledOrders(): void {
		this._ordersStocksService.getAllOrders(this.orderStateEnum.CANCELED).pipe(takeUntil(this._destroy$)).subscribe()
	}

	private _updateStatesColumns(): void {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return

		this.statesColumns.set([...widget.settings.stocks.canceled.columns])
	}

	protected onPageChange(newPage: number): void {
		const ordersParams$: BehaviorSubject<OrderParamsInterface> | undefined =
			this._ordersStocksHelperService.ordersParamsMap.get(this.orderStateEnum.CANCELED)
		if (!ordersParams$) return

		ordersParams$.next({ ...ordersParams$.getValue(), page: newPage })

		this._getCanceledOrders()
		this.isLoadingOrders.set(true)
	}

	protected setLiveTicker(symbol: string): void {
		this._widgetsService.setLiveTickerToDashboard(symbol)
		this._liveTickerService.launchLiveTicker(symbol)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['widget']) this._updateStatesColumns()
	}

	ngOnInit(): void {
		this._listenSecret()
		this._launchCanceledOrders()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
